<script setup lang="ts">
import type { NuxtError } from "#app";

const { error, layout = "default" } = defineProps<{
  error: NuxtError;
  layout?: string;
}>();

const { public: publicConfig } = useRuntimeConfig();

if (import.meta.server) {
  console.error(error);
}

const { t } = useI18n({
  messages: {
    en: {
      "400": {
        title: "Bad Request",
        message:
          "Oh oh... Uh-oh, looks like our wires got a little crossed. It could be either our own fault, or you just tried something very naughty!\n\nStill however, let's return to safety with the button below.",
      },
      "401": {
        title: "Unauthorized",
        message:
          "Hold up! Just like you cannot enter any hotel room without a key, you need to be authenticated to perform this action!\n\nHead over back to the homepage and try to log in before our bouncers can let you in. Click the button below.",
      },
      "403": {
        title: "Forbidden",
        message:
          "Woah there, partner! Looks like you've stumbled into a VIP-only zone.\n\nThis content is a bit hush-hush and not quite ready for public viewing (or, at least, not for your eyes right now). Think of it as a secret rooftop bar, and you don't have the key... yet.\n\nIf you believe you should have access, or if you're trying to reach something specific, double-check your login or permissions. Sometimes a quick refresh can work wonders, too.\n\nStill no luck? Our digital bouncers (also known as the support team) can help you figure out what's going on. Just give them a shout! In the mean time you can always head back to the homepage with the button below.",
      },
      "404": {
        title: "Page not found",
        message:
          "It's not you... It's us.\n\nThis page does not exist (anymore), click the button to return home.",
      },
      "500": {
        title: "Internal Server Error",
        message:
          "Oops, we seem to have a small glitch in the system. Our friendly robots are working on it.\n\nPlease try again in a bit, or maybe try the homepage with the button below.",
      },
      cta: "Go to homepage",
    },
    nl: {
      "400": {
        title: "Ongeldige aanvraag",
        message:
          "Oeps... Het lijkt erop dat er iets mis is gegaan. Het kan aan ons liggen, of je hebt iets geprobeerd wat niet helemaal de bedoeling was!\n\nLaten we voor de zekerheid maar teruggaan met de knop hieronder.",
      },
      "401": {
        title: "Niet geautoriseerd",
        message:
          "Wacht even! Zoals je geen hotelkamer binnenkomt zonder sleutel, moet je geauthenticeerd zijn om deze actie uit te voeren!\n\nGa terug naar de homepage en probeer in te loggen voordat we je binnen kunnen laten. Klik op de knop hieronder.",
      },
      "403": {
        title: "Verboden",
        message:
          "Wacht eens even! Het lijkt erop dat je in een VIP-zone bent beland.\n\nDeze inhoud is een beetje geheim en niet voor het grote publiek bedoeld (of in ieder geval niet voor jouw ogen op dit moment). Zie het als een geheime rooftop bar waarvoor jij... nog geen sleutel hebt.\n\nDenk je dat je wel toegang zou moeten hebben, of probeer je iets specifieks te bereiken? Controleer dan even je login of rechten. Soms helpt een snelle refresh ook al.\n\nNog steeds geen toegang? Onze digitale uitsmijters (ook wel het supportteam genoemd) kunnen je verder helpen. Neem gerust contact met ze op! In de tussentijd kun je altijd terug naar de homepage via de knop hieronder.",
      },
      "404": {
        title: "Pagina niet gevonden",
        message:
          "Het ligt niet aan jou... Het ligt aan ons.\n\nDeze pagina bestaat (meer) niet, klik op de knop om terug te gaan naar de homepage.",
      },
      "500": {
        title: "Interne serverfout",
        message:
          "Oeps, we hebben een klein probleem in het systeem. Onze vriendelijke robots zijn er mee bezig.\n\nProbeer het over een tijdje nog eens, of ga naar de homepage met de knop hieronder.",
      },
      cta: "Ga naar de homepage",
    },
    fr: {
      "400": {
        title: "Requête incorrecte",
        message:
          "Oh oh... Oups, il semble que nos fils se soient un peu emmêlés. Cela peut être de notre faute, ou vous avez juste essayé quelque chose de très coquin !\n\nQuoi qu'il en soit, retournons en sécurité avec le bouton ci-dessous.",
      },
      "401": {
        title: "Non autorisé",
        message:
          "Attendez ! Tout comme vous ne pouvez pas entrer dans une chambre d'hôtel sans clé, vous devez être authentifié pour effectuer cette action !\n\nRetournez à la page d'accueil et essayez de vous connecter avant que nos videurs ne puissent vous laisser entrer. Cliquez sur le bouton ci-dessous.",
      },
      "403": {
        title: "Interdit",
        message:
          "Whoa là ! Il semble que vous soyez tombé dans une zone réservée aux VIP.\n\nCe contenu est un peu confidentiel et pas tout à fait prêt pour le grand public (ou du moins, pas pour vos yeux pour l'instant). Considérez cela comme un bar sur le toit secret, et vous n'avez pas encore la clé...\n\nSi vous pensez que vous devriez avoir accès, ou si vous essayez d'atteindre quelque chose de spécifique, vérifiez votre connexion ou vos autorisations. Parfois, un simple rafraîchissement peut faire des merveilles aussi.\n\nToujours pas de chance ? Nos videurs numériques (également connus sous le nom d'équipe de support) peuvent vous aider à comprendre ce qui se passe. Il suffit de leur faire signe ! En attendant, vous pouvez toujours retourner à la page d'accueil avec le bouton ci-dessous.",
      },
      "404": {
        title: "Page non trouvée",
        message:
          "Ce n'est pas vous... C'est nous.\n\nCette page n'existe pas (plus), cliquez sur le bouton pour retourner à la page d'accueil.",
      },
      "500": {
        title: "Erreur interne du serveur",
        message:
          "Oups, nous semblons avoir un petit bug dans le système. Nos robots sympathiques travaillent dessus.\n\nVeuillez réessayer dans un moment, ou peut-être essayer la page d'accueil avec le bouton ci-dessous.",
      },
      cta: "Aller à la page d'accueil",
    },
    zh: {
      "400": {
        title: "错误请求",
        message:
          "哦哦... 看起来我们的线路有点混乱。这可能是我们的错，或者你尝试了些不太合适的事情！\n\n无论如何，让我们通过下面的按钮返回安全地带。",
      },
      "401": {
        title: "未授权",
        message:
          "等等！就像你不能没有钥匙进入任何酒店房间一样，你需要经过身份验证才能执行此操作！\n\n返回首页并尝试登录，然后我们的保镖就可以让你进入。点击下面的按钮。",
      },
      "403": {
        title: "禁止访问",
        message:
          "哇哦！看起来你误入了VIP专属区。\n\n这个内容有点保密，还没有准备好供公众查看（或者至少现在不适合你的眼睛）。把它想象成一个秘密屋顶酒吧，而你还没有钥匙。\n\n如果你认为自己应该有访问权限，或者你正在尝试访问某些特定内容，请检查你的登录或权限。有时候，快速刷新也能解决问题。\n\n还是不行？我们的数字保镖（也称为支持团队）可以帮助你弄清楚发生了什么。只需给他们发个信号！与此同时，你可以随时通过下面的按钮返回首页。",
      },
      "404": {
        title: "页面未找到",
        message:
          "不是你的错... 是我们的错。\n\n这个页面不存在（了），点击按钮返回首页。",
      },
      "500": {
        title: "内部服务器错误",
        message:
          "哎呀，我们似乎在系统中遇到了小问题。我们的友好机器人正在处理。\n\n请稍后再试，或者尝试使用下面的按钮访问首页。",
      },
      cta: "返回首页",
    },
    es: {
      "400": {
        title: "Solicitud incorrecta",
        message:
          "Oh oh... Parece que nuestros cables se han cruzado un poco. Puede ser culpa nuestra, o simplemente has intentado algo muy travieso.\n\nDe todos modos, volvamos a la seguridad con el botón de abajo.",
      },
      "401": {
        title: "No autorizado",
        message:
          "¡Espera! Al igual que no puedes entrar en ninguna habitación de hotel sin una llave, necesitas estar autenticado para realizar esta acción.\n\nVuelve a la página de inicio e intenta iniciar sesión antes de que nuestros porteros puedan dejarte entrar. Haz clic en el botón de abajo.",
      },
      "403": {
        title: "Prohibido",
        message:
          "¡Whoa! Parece que has tropezado con una zona exclusiva para VIP.\n\nEste contenido es un poco confidencial y aún no está listo para el público en general (o al menos, no para tus ojos en este momento). Piensa en ello como un bar secreto en la azotea, y aún no tienes la llave...\n\nSi crees que deberías tener acceso, o si estás intentando llegar a algo específico, verifica tu inicio de sesión o permisos. A veces, una rápida actualización puede hacer maravillas también.\n\n¿Aún sin suerte? Nuestros porteros digitales (también conocidos como el equipo de soporte) pueden ayudarte a averiguar qué está pasando. ¡Solo dales un toque! Mientras tanto, siempre puedes volver a la página de inicio con el botón de abajo.",
      },
      "404": {
        title: "Página no encontrada",
        message:
          "El error es nuestro…\n\nEsta página (ya) no existe, haz clic en el botón para volver al inicio.",
      },
      "500": {
        title: "Error interno del servidor",
        message:
          "Vaya, parece que tenemos un pequeño fallo en el sistema. Nuestros robots amigables están trabajando en ello.\n\nInténtalo de nuevo en un momento, o tal vez prueba la página de inicio con el botón de abajo.",
      },
      cta: "Ir a la página de inicio",
    },
    it: {
      "400": {
        title: "Richiesta non valida",
        message:
          "Oh oh... Sembra che i nostri fili si siano un po' incrociati. Potrebbe essere colpa nostra, o hai provato qualcosa di molto birichino!\n\nIn ogni caso, torniamo in sicurezza con il pulsante qui sotto.",
      },
      "401": {
        title: "Non autorizzato",
        message:
          "Aspetta! Proprio come non puoi entrare in nessuna camera d'hotel senza una chiave, devi essere autenticato per eseguire questa azione!\n\nTorna alla homepage e prova a effettuare il login prima che i nostri buttafuori possano lasciarti entrare. Clicca sul pulsante qui sotto.",
      },
      "403": {
        title: "Accesso vietato",
        message:
          "Whoa! Sembra che tu sia inciampato in una zona riservata ai VIP.\n\nQuesto contenuto è un po' riservato e non è ancora pronto per il pubblico (o almeno, non per i tuoi occhi in questo momento). Pensalo come un bar segreto sul tetto, e tu non hai ancora la chiave...\n\nSe pensi di dover avere accesso, o se stai cercando di raggiungere qualcosa di specifico, controlla il tuo login o i permessi. A volte un rapido aggiornamento può fare miracoli.\n\nAncora senza fortuna? I nostri buttafuori digitali (conosciuti anche come team di supporto) possono aiutarti a capire cosa sta succedendo. Basta dargli un colpo! Nel frattempo, puoi sempre tornare alla homepage con il pulsante qui sotto.",
      },
      "404": {
        title: "Pagina non trovata",
        message:
          "Non è colpa tua... È colpa nostra.\n\nQuesta pagina non esiste (più), clicca sul pulsante per tornare alla homepage.",
      },
      "500": {
        title: "Errore interno del server",
        message:
          "Ops, sembra che ci sia un piccolo problema nel sistema. I nostri robot amichevoli ci stanno lavorando.\n\nPer favore, riprova tra un po', o magari prova la homepage con il pulsante qui sotto.",
      },
      cta: "Torna alla homepage",
    },
  },
});
const localePath = useLocalePath();

const showStack = computed(() => import.meta.env.DEV);
const renderedLayout = computed(() =>
  String(publicConfig.errorPageLayout || layout),
);
</script>

<template>
  <NuxtLayout :name="renderedLayout">
    <div class="page-error">
      <div class="container page-error__content">
        <h1>{{ t(`${error.statusCode}.title`) }}</h1>
        <p>{{ t(`${error.statusCode}.message`) }}</p>
        <FoundationBaseButton class="page-error__cta" :to="localePath('/')">
          {{ t("cta") }}
        </FoundationBaseButton>
        <pre v-if="showStack" class="page-error__stack">
          {{ error.stack }}
        </pre>
      </div>
    </div>
  </NuxtLayout>
</template>

<style>
.page-error {
  margin-block: var(--spacing-8, 2rem);
}

.page-error__cta {
  margin-block-start: var(--spacing-6, 1.5rem);
}

.page-error__content p {
  max-width: 80ch;
  margin-block: var(--spacing-2, 0.5rem);
  white-space: pre-line;
}

.page-error__content h1 {
  font-weight: bold;
  font-size: var(--font-size-h5, 3rem);
  font-family: var(--font-family-heading);
  line-height: 1.6;
  margin-block-end: 0;
}

.page-error__stack {
  max-width: 100%;
  overflow: scroll;
}
</style>
